import {useState, useMemo} from 'react'

type VariationType = {
  id: number
  context: string
}

type AdditionalType = {
  code: string
  name: string
  price: number
  quantity: number
}

type FractionalType = {
  name: string
}

type MeasureType = {
  width: number
  height: number
  lenght: number
  weight: number
}

type ProductContentType = {
  productName: string
  image?: string
  observation?: string
  additionals: AdditionalType[]
  fractionated: FractionalType[]
  measures: MeasureType | null
}

type ProductType = {
  id: number
  sku: string
  quantity: number
  price: number
  discount: string
  skuLocalVariation: VariationType
  variationHtml?: string
  additionalHtml?: string
  fractionalHtml?: string
  content: ProductContentType
}

export interface UseProductFilterReturn {
  filteredProducts: ProductType[]
  searchTerm: string
  setSearchTerm: (term: string) => void
  clearSearch: () => void
  totalResults: number
  hasActiveFilter: boolean
}

/**
 * Hook customizado para filtrar produtos por nome ou SKU
 * @param products - Array de produtos a filtrar
 * @returns Objeto com produtos filtrados e métodos de filtro
 */
export const useProductFilter = (products: ProductType[]): UseProductFilterReturn => {
  const [searchTerm, setSearchTerm] = useState<string>('')

  // Filtrar produtos baseado no termo de busca
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return products
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim()

    return products.filter((product) => {
      const sku = product.sku.toLowerCase()
      const productName = product.content.productName.toLowerCase()
      const priceStr = (product.price / 100).toFixed(2)

      // Busca por SKU, nome do produto ou valor
      return (
        sku.includes(lowerSearchTerm) ||
        productName.includes(lowerSearchTerm) ||
        priceStr.includes(lowerSearchTerm)
      )
    })
  }, [products, searchTerm])

  // Limpar busca
  const clearSearch = () => {
    setSearchTerm('')
  }

  return {
    filteredProducts,
    searchTerm,
    setSearchTerm,
    clearSearch,
    totalResults: filteredProducts.length,
    hasActiveFilter: searchTerm.trim().length > 0,
  }
}
